import { useEffect, useState } from "react";
import { mutate } from "swr";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@mui/material";
import {
  getClearProviderModel,
  getClientFilteringOptions,
} from "../../common/EntitiesManagement";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import AddOrEditProvider from "./components/AddOrEditProvider";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDatalabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Provider() {
  const {
    PROVIDER_ENDPOINT,
    providerModel,
    setProviderModel,
    providersData,
    config,
    pageIndexProviders,
    setPageIndexProviders,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    setCity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [providerDataFiltered, setProviderDataFiltered] = useState(
    providersData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getClientFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const providerDataObj = providersData.Data.find(
        (providerObj) => providerObj.Id === Number(id)
      );
      setProviderModel(providerDataObj);
    }
  }, [id]);

  useEffect(() => {
    setProviderDataFiltered(providersData.Data);
  }, [providersData]);

  const validateOperationResult = (result, isDelete) => {
    setProviderModel(getClearProviderModel());
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([PROVIDER_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexProviders !== 0 && providersData.Data.length === 1) {
        setPageIndexContractors(pageIndexProviders - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${PROVIDER_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${PROVIDER_ENDPOINT}/${data.Id}`}`,
      {
        ...data,
        IdentificationType: data.IdentificationType.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${PROVIDER_ENDPOINT}`}`,
      {
        ...data,
        IdentificationType: data.IdentificationType.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !providerModel.IdentificationType ||
    !providerModel.Name ||
    providerModel.Name.replaceAll(" ", "").length <= 0 ||
    !providerModel.Identification ||
    providerModel.Identification.replaceAll(" ", "").length <= 0 ||
    !providerModel.Address ||
    providerModel.Address.replaceAll(" ", "").length <= 0 ||
    !providerModel.PhoneNumber ||
    providerModel.PhoneNumber.replaceAll(" ", "").length <= 0 ||
    providerModel.StateId <= 0 ||
    providerModel.CityId <= 0;

  const getColumnTitles = () => {
    return [
      localize.identification_type,
      localize.identification,
      localize.name,
      localize.phone_number,
      localize.email,
      localize.contact_name,
      localize.address,
      localize.department,
      localize.city,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.providers}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={PROVIDER_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="provider"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_provider}
          isEditForm
          onClickToSubmit={async () => {
            await handleUpdateRequest(providerModel);
            setShowEditModal(false);
            setCity(null);

            setProviderModel(getClearProviderModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setCity(null);

            setProviderModel(getClearProviderModel());
          }}
          Component={<AddOrEditProvider />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.providers}
            subtitle={localize.providers_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="providerr_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_provider}
                onClickToSubmit={async () => {
                  await handleCreateRequest(providerModel);
                  setShowAddModal(false);
                  setCity(null);
                  setProviderModel(getClearProviderModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setCity(null);
                  setProviderModel(getClearProviderModel());
                }}
                Component={<AddOrEditProvider />}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={providerModel.Name}
        entityType={localize.provider}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setProviderModel(getClearProviderModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(providerModel.Id);
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexProviders}
            setPageIndex={setPageIndexProviders}
            dataArray={providersData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          providersData.Data?.length !== 0
            ? providerDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setProviderModel({
                          ...item,
                          IdentificationType: {
                            Id: item.IdentificationType,
                            Name: item.IdentificationType,
                          },
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setProviderModel({
                          ...item,
                          IdentificationType: {
                            Id: item.IdentificationType,
                            Name: item.IdentificationType,
                          },
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                        });
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.IdentificationType,
                  item.Identification,
                  item.Name,
                  <ValueOrNoDatalabel data={item.PhoneNumber} />,
                  <ValueOrNoDatalabel data={item.Email} />,
                  <ValueOrNoDatalabel data={item.ContactName} />,
                  <ValueOrNoDatalabel data={item.Address} />,
                  item.State.Name,
                  item.City.Name,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(12)]
        }
      />
    </div>
  );
}
